import { useEffect, useState } from 'react';
import type { DataTableCellStateChangeEvent, DataTableErrors } from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';
import { fromCellRef } from '../DataTable.util';
import { useDataTable } from '../DataTableProvider';

/**
 * Subscribe to data table validation errors
 *
 * This hook manages the list of validation errors across the entire data table.
 * It subscribes to CellStateChange events and transforms invalid cells into
 * an errors array with coordinates and status information. Uses AbortController for cleanup.
 *
 * @returns Array of data table errors
 */
export const useDataTableErrors = (): DataTableErrors => {
  const { dataTableService } = useDataTable();
  const [errors, setErrors] = useState<DataTableErrors>([]);

  useEffect(() => {
    const abortController = new AbortController();

    const cellStateChangeListener = (event: DataTableCellStateChangeEvent) => {
      setErrors((prevErrors) => {
        if (
          prevErrors.length !== event.detail.invalidCells.size ||
          prevErrors.some(({ ref }) => !event.detail.invalidCells.has(ref))
        ) {
          return [...event.detail.invalidCells].map((ref) => {
            const coordinate = fromCellRef(ref);
            return {
              ref,
              coordinate,
              status: dataTableService.cellStatus(coordinate),
            };
          });
        }
        return prevErrors;
      });
    };

    dataTableService.subscribe(DataTableEvent.CellStateChange, cellStateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService]);

  return errors;
};
